// @ts-nocheck
import useGetCurrentProduct from "@store/Framework/Store/Data/Products/ProductsHooks/useGetCurrentProduct";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import translate from "@insite/client-framework/Translate";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import LoadingOverlay from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@store/MobiusOverrides/Modal/Modal";
import useTrackEvent from "@toolkit/Framework/Common/GoogleAnalytics/hooks/useTrackEvent";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import React from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";
import Info from "@insite/mobius/Icons/Info";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import DataTable, { DataTablePresentationProps } from "@insite/mobius/DataTable";
import { DataTableBody, DataTableCell, DataTableRow } from "@insite/mobius/index";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import getColor from "@insite/mobius/utilities/getColor";
import { StyledCol } from "@store/Library/Components/Common/StyledCol";
import Button from "@store/MobiusOverrides/Button/Button";
import { ButtonIcon, ButtonPresentationProps } from "@store/MobiusOverrides/Button/Props";
import { AttributeTypeModel } from "@insite/client-framework/Types/ApiModels";
import constants from "@store/constants";

interface MyListDetailsSpecificationModalProps {
    extendedStyles?: MyListDetailsSpecificationModalStyles;
}

interface MyListDetailsSpecificationModalStyles {
    modal?: ModalPresentationProps;
    button?: ButtonPresentationProps;
    loadingOverlay?: InjectableCss;
    dataTable?: DataTablePresentationProps;
    cell?: DataTableCellProps;
    labelTypographyProps?: DataTableCellProps["typographyProps"];
}

const myListDetailsSpecificationModalStyles: MyListDetailsSpecificationModalStyles = {
    labelTypographyProps: {
        css: css`
            font-weight: ${({ theme }) => theme.design.typography.weight.semibold};
        `,
    },
    cell: {
        css: css`
            border: 1px solid ${getColor("common.border")};
        `,
    },

    button: {
        mergeCss: true,
        css: css`
            color: ${getColor("success")};
            background-color: ${getColor("success.light")};
            min-height: ${pxToRem(26)};
            border: none;
            :hover:not(:disabled) {
                background-color: ${getColor("success.light")};
                color: ${getColor("success")};
                outline: 1px solid ${getColor("success.main")};
            }
            font-weight: ${({ theme }) => theme.design.typography.weight.light};
            border-radius: ${pxToRem(12)};
            padding: 0 ${pxToRem(10)};
            ${ButtonIcon} {
                color: inherit;
                width: ${pxToRem(15)};
                height: ${pxToRem(15)};
                margin-right: ${pxToRem(4)};
                > svg {
                    height: ${pxToRem(15)};
                    width: ${pxToRem(15)};
                }
            }
        `,
        icon: {
            position: "left",
            src: Info,
        },

        typographyProps: {
            italic: true,
            css: css`
                font-size: ${({ theme }) => theme.design.typography.size.smallest};
            `,
        },
    },
    loadingOverlay: {
        css: css`
            width: fit-content;
        `,
    },
};

const filterAttributeTypes = (attributeTypes: AttributeTypeModel[]) =>
    attributeTypes.filter(attributeType => attributeType.name !== constants.attributeTypes.BCHVendorSku);

const MyListDetailsSpecificationModal: React.FC<MyListDetailsSpecificationModalProps> = ({ extendedStyles }) => {
    const styles = useMergeProps(myListDetailsSpecificationModalStyles, extendedStyles);
    const wishListProduct = useGetCurrentProduct();
    const fullProduct = useAppSelector(state => state.data.products.byId[wishListProduct?.product.id]);
    const isProductLoading = useAppSelector(
        state => state.data.products.isLoading?.[wishListProduct?.product.id] ?? false,
    );
    const [isOpen, setIsOpen] = React.useState(false);
    const trackEvent = useTrackEvent({
        action: "Click",
        category: "Wish List",
        label: "Specification Modal Opened",
        item_id: wishListProduct?.product.id,
    });

    const dispatch = useDispatch();
    const handleOpenModal = () => {
        trackEvent();
        setIsOpen(!isOpen);
        if (!fullProduct) {
            dispatch(loadProduct({ id: wishListProduct?.product.id, expand: ["specifications"] }));
        }
    };
    if (!wishListProduct) {
        return null;
    }
    const filteredAttributes = filterAttributeTypes(fullProduct?.attributeTypes ?? []);
    return (
        <LoadingOverlay loading={isProductLoading} {...styles.loadingOverlay}>
            <Button {...styles.button} onClick={handleOpenModal}>
                {translate("Specifications")}
            </Button>
            {fullProduct && (
                <Modal
                    isOpen={isOpen}
                    headline={translate("Specifications")}
                    handleClose={handleOpenModal}
                    {...styles.modal}
                >
                    <DataTable {...styles.dataTable}>
                        <colgroup>
                            <StyledCol width={50} />
                            <StyledCol width={50} />
                        </colgroup>
                        <DataTableBody>
                            <DataTableRow>
                                <DataTableCell {...styles.cell} typographyProps={styles.labelTypographyProps}>
                                    {translate("Brand")}
                                </DataTableCell>
                                <DataTableCell {...styles.cell}>{fullProduct.brand?.name}</DataTableCell>
                            </DataTableRow>
                            {filteredAttributes.map(attributeType => (
                                <DataTableRow key={attributeType.id.toString()}>
                                    <DataTableCell {...styles.cell} typographyProps={styles.labelTypographyProps}>
                                        {attributeType.label || attributeType.name}
                                    </DataTableCell>
                                    <DataTableCell {...styles.cell}>
                                        {(attributeType.attributeValues ?? [])
                                            .map(attributeValue => attributeValue.valueDisplay)
                                            .join(", ")}
                                    </DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                </Modal>
            )}
        </LoadingOverlay>
    );
};

export default MyListDetailsSpecificationModal;
